import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Truck,
  X,
  CheckCircle,
  Eye,
  Clock,
  AlertCircle,
  ShoppingCart,
} from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "./index.css";
import { useDistributeOrdersMutation } from "../../../../redux/services/DistributionService";
import { Cart, ProductSKU } from "../../../../redux/distributionSlice";

interface AllocationItem {
  id: string;
  name: string;
  allocation: number;
  unitPrice: number;
  portionSize?: string;
  menuItemType?: string;
}

interface OrderSummary {
  cartId: string;
  cartName: string;
  priority: string;
  items: AllocationItem[];
  totalQuantity: number;
  totalValue: number;
  status: string;
  selected: boolean;
}

interface DistributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  carts: Cart[];
  products: ProductSKU[];
  allocations: Record<string, Record<string, number>>;
  preOrders: Record<string, Record<string, number>>;
  onDistributionSuccess: (cartIds: string[]) => void;
}

const DistributionModal: React.FC<DistributionModalProps> = ({
  isOpen,
  onClose,
  carts,
  products,
  allocations,
  preOrders,
  onDistributionSuccess,
}) => {
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [orderStatus, setOrderStatus] = useState<Record<string, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);

  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const selectedRestaurantData = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails
  );

  const [distributeOrders] = useDistributeOrdersMutation();

  // Reset modal state when it opens
  const resetModalState = useCallback(() => {
    setSelectedOrders(new Set());
    setOrderStatus({});
    setIsProcessing(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetModalState();
    }
  }, [isOpen, resetModalState]);

  const handleClose = useCallback(() => {
    resetModalState();
    onClose();
  }, [resetModalState, onClose]);

  const generateOrderSummary = useCallback((): OrderSummary[] => {
    return carts
      .filter((cart) => !cart.isOrderPlaced)
      .map((cart) => {
        const items: AllocationItem[] = [];
        let totalQuantity = 0;
        let totalValue = 0;

        products.forEach((product) => {
          const allocation = allocations[product.id]?.[cart.id] || 0;

          if (allocation > 0) {
            const item: AllocationItem = {
              id: product.id,
              name: product.name,
              allocation,
              unitPrice: product.unitPrice,
              portionSize: product.portionSize,
              menuItemType: product.menuItemType || "",
            };
            items.push(item);
            totalQuantity += allocation;
            totalValue += allocation * item.unitPrice;
          }
        });

        return {
          cartId: cart.id,
          cartName: cart.name,
          priority: cart.priority,
          items,
          totalQuantity,
          totalValue,
          status: orderStatus[cart.id] || "pending",
          selected: selectedOrders.has(cart.id),
        };
      })
      .filter((order) => order.items.length > 0);
  }, [carts, products, allocations, orderStatus, selectedOrders]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High":
        return "distribution-priority-high";
      case "Medium":
        return "distribution-priority-medium";
      case "Low":
        return "distribution-priority-low";
      default:
        return "distribution-priority-default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "processing":
        return <Clock className="distribution-status-processing" size={16} />;
      case "confirmed":
        return (
          <CheckCircle className="distribution-status-confirmed" size={16} />
        );
      case "error":
        return <AlertCircle className="distribution-status-error" size={16} />;
      default:
        return <Eye className="distribution-status-pending" size={16} />;
    }
  };

  const toggleOrderSelection = useCallback((cartId: string) => {
    setSelectedOrders((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(cartId)) {
        newSelection.delete(cartId);
      } else {
        newSelection.add(cartId);
      }
      return newSelection;
    });
  }, []);

  const selectAllOrders = useCallback(() => {
    const orders = generateOrderSummary();
    const pendingOrders = orders.filter((order) => order.status === "pending");
    setSelectedOrders(new Set(pendingOrders.map((order) => order.cartId)));
  }, [generateOrderSummary]);

  const clearOrderSelection = useCallback(() => {
    setSelectedOrders(new Set());
  }, []);

  const createOrderData = useCallback(
    (order: OrderSummary, cart: Cart) => {
      const orderItems = order.items.map((item) => {
        const baseProductId = item.id.split("-").slice(0, -1).join("-");
        return {
          menuItemId: baseProductId,
          displayName: item.name,
          itemType: item.menuItemType || "",
          quantity: item.allocation,
          unitPrice: item.unitPrice,
          portionSize: item.portionSize || "REGULAR",
        };
      });

      return {
        customerId: "",
        cartId: order.cartId,
        restaurantId: selectedRestaurantId,
        displayName: cart.name,
        address:
          cart.address || selectedRestaurantData?.address || "No Address",
        phoneNumber:
          cart.phoneNumber || selectedRestaurantData?.phoneNumber || "",
        imageUrl: cart.imageUrl || selectedRestaurantData?.imageUrl || "",
        paymentMethod: "CASH",
        orderCreatedBy: "Restaurant",
        deliveryTime: new Date().toISOString(),
        isScheduledOrder: false,
        totalPrice: order.totalValue,
        orderItems: orderItems,
      };
    },
    [selectedRestaurantId, selectedRestaurantData]
  );

  const createDistributeRequest = useCallback(
    (ordersToProcess: OrderSummary[]) => {
      const ordersData = ordersToProcess.map((order) => {
        const cart = carts.find((c) => c.id === order.cartId);
        return createOrderData(order, cart || carts[0]);
      });

      return {
        orders: ordersData,
      };
    },
    [carts, createOrderData]
  );

  const placeOrder = useCallback(
    async (cartId: string) => {
      setIsProcessing(true);
      setOrderStatus((prev) => ({ ...prev, [cartId]: "processing" }));

      try {
        const orders = generateOrderSummary();
        const order = orders.find((o) => o.cartId === cartId);

        if (!order) {
          throw new Error("Order not found");
        }

        const request = createDistributeRequest([order]);
        const result = await distributeOrders(request).unwrap();

        if (result) {
          setOrderStatus((prev) => ({ ...prev, [cartId]: "confirmed" }));
          toast.success(
            `Order for ${order.cartName} distributed successfully!`
          );
          onDistributionSuccess([cartId]);

          // Remove from selection after successful distribution
          setSelectedOrders((prev) => {
            const newSet = new Set(prev);
            newSet.delete(cartId);
            return newSet;
          });
        }
      } catch (error: any) {
        console.error("Error distributing order:", error);
        setOrderStatus((prev) => ({ ...prev, [cartId]: "error" }));
        toast.error(
          `Failed to distribute order for ${cartId}: ${error.message}`
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [
      distributeOrders,
      generateOrderSummary,
      createDistributeRequest,
      onDistributionSuccess,
    ]
  );

  const placeSelectedOrders = useCallback(async () => {
    // Get current state values directly instead of relying on generateOrderSummary
    const ordersToProcess = carts
      .filter(
        (cart) =>
          !cart.isOrderPlaced &&
          selectedOrders.has(cart.id) &&
          (orderStatus[cart.id] === "pending" || !orderStatus[cart.id])
      )
      .map((cart) => {
        const items: AllocationItem[] = [];
        let totalQuantity = 0;
        let totalValue = 0;

        products.forEach((product) => {
          const allocation = allocations[product.id]?.[cart.id] || 0;
          if (allocation > 0) {
            items.push({
              id: product.id,
              name: product.name,
              allocation,
              unitPrice: product.unitPrice,
              portionSize: product.portionSize,
              menuItemType: product.menuItemType || "",
            });
            totalQuantity += allocation;
            totalValue += allocation * product.unitPrice;
          }
        });

        return {
          cartId: cart.id,
          cartName: cart.name,
          priority: cart.priority,
          items,
          totalQuantity,
          totalValue,
          status: orderStatus[cart.id] || "pending",
          selected: true,
        };
      })
      .filter((order) => order.items.length > 0);

    if (ordersToProcess.length === 0) {
      toast.info("No valid orders to process");
      return;
    }

    setIsProcessing(true);

    try {
      const request = createDistributeRequest(ordersToProcess);

      const result = await distributeOrders(request).unwrap();

      if (result) {
        const newStatus: Record<string, string> = {};
        ordersToProcess.forEach((order) => {
          newStatus[order.cartId] = "confirmed";
        });
        setOrderStatus((prev) => ({ ...prev, ...newStatus }));

        toast.success(
          `Successfully distributed ${ordersToProcess.length} orders!`
        );
        onDistributionSuccess(ordersToProcess.map((o) => o.cartId));

        // Clear selection after successful distribution
        setSelectedOrders(new Set());
      } else {
        throw new Error("Distribution failed");
      }
    } catch (error: any) {
      console.error("Error distributing orders:", error);
      const newStatus: Record<string, string> = {};
      ordersToProcess.forEach((order) => {
        newStatus[order.cartId] = "error";
      });
      setOrderStatus((prev) => ({ ...prev, ...newStatus }));
      toast.error(`Failed to distribute orders: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [
    carts,
    products,
    allocations,
    selectedOrders,
    orderStatus,
    createDistributeRequest,
    distributeOrders,
    onDistributionSuccess,
  ]);

  const orders = useMemo(() => generateOrderSummary(), [generateOrderSummary]);
  const pendingOrdersCount = useMemo(
    () => orders.filter((order) => order.status === "pending").length,
    [orders]
  );

  if (!isOpen) return null;

  return (
    <div className="distribution-modal-overlay">
      <div className="distribution-modal-container">
        {/* Modal Header */}
        <div className="distribution-modal-header">
          <div className="distribution-modal-header-left">
            <Truck className="distribution-modal-icon" />
            <div>
              <h2 className="distribution-modal-title">Place Order(s) </h2>
              <p className="distribution-modal-subtitle"></p>
            </div>
          </div>
          <div className="distribution-modal-header-right">
            <div className="distribution-modal-select-all">
              <input
                type="checkbox"
                checked={
                  pendingOrdersCount > 0 &&
                  selectedOrders.size === pendingOrdersCount
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    selectAllOrders();
                  } else {
                    clearOrderSelection();
                  }
                }}
                className="distribution-checkbox"
                disabled={pendingOrdersCount === 0}
              />
              <span className="distribution-select-all-text">Select All</span>
            </div>
            <span className="distribution-selected-count">
              {selectedOrders.size} of {pendingOrdersCount} orders selected
            </span>
            <button
              onClick={placeSelectedOrders}
              disabled={isProcessing || selectedOrders.size === 0}
              className="distribution-place-orders-btn"
            >
              <CheckCircle size={16} />
              {isProcessing
                ? `Placing ${selectedOrders.size} Orders...`
                : `Place Order(s) (${selectedOrders.size})`}
            </button>
            <button onClick={handleClose} className="distribution-close-btn">
              <X size={16} />
              Close
            </button>
          </div>
        </div>

        {/* Orders List */}
        <div className="distribution-orders-list">
          {orders.length === 0 ? (
            <div className="distribution-empty-state">
              <ShoppingCart className="distribution-empty-icon" />
              <h3 className="distribution-empty-title">
                No Balance Orders.
              </h3>
              <p className="distribution-empty-description">
                No carts have any balance allocations. Only pre-orders exist or
                no allocations made yet.
              </p>
              <p className="distribution-empty-note">
                Balance orders are for newly allocated products (excluding
                pre-orders)
              </p>
            </div>
          ) : (
            orders.map((order) => {
              const isSelected = selectedOrders.has(order.cartId);
              const isDisabled =
                order.status === "confirmed" ||
                order.status === "processing" ||
                order.status === "error";

              return (
                <div
                  key={order.cartId}
                  className={`distribution-order-card ${
                    isSelected && !isDisabled
                      ? "distribution-order-selected"
                      : ""
                  } ${isDisabled ? "distribution-order-disabled" : ""}`}
                >
                  {orders.length === 0 &&
                    carts.some((cart) => cart.isOrderPlaced) && (
                      <div className="distribution-excluded-carts-notice">
                        <span>
                          {carts.filter((cart) => cart.isOrderPlaced).length}{" "}
                          cart(s) with placed orders are excluded from
                          distribution
                        </span>
                      </div>
                    )}

                  {/* Cart Header */}
                  <div className="distribution-order-header">
                    <div className="distribution-order-header-left">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => toggleOrderSelection(order.cartId)}
                        className="distribution-order-checkbox"
                      />
                      <div>
                        <h3 className="distribution-order-title">
                          {order.cartName}
                        </h3>
                        <div className="distribution-order-meta">
                          <span>ID: {order.cartId}</span>
                          <span
                            className={`distribution-priority-badge ${getPriorityColor(
                              order.priority
                            )}`}
                          >
                            {order.priority} Priority
                          </span>
                          <span className="distribution-order-balance">
                            Items: {order.totalQuantity.toLocaleString()}
                          </span>
                          <span className="distribution-order-value">
                            Value: Rs. {order.totalValue.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="distribution-order-header-right">
                      <div className="distribution-order-status">
                        {getStatusIcon(order.status)}
                        <span className="distribution-status-text">
                          {order.status}
                        </span>
                      </div>
                      <button
                        onClick={() => placeOrder(order.cartId)}
                        disabled={
                          isProcessing ||
                          order.status === "confirmed" ||
                          order.status === "processing"
                        }
                        className="distribution-order-place-btn"
                      >
                        <CheckCircle size={14} />
                        {order.status === "confirmed"
                          ? "Placed"
                          : order.status === "error"
                          ? "Retry"
                          : "Place Order"}
                      </button>
                    </div>
                  </div>

                  {/* Items Details */}
                  <div className="distribution-order-items">
                    <h4 className="distribution-items-title">
                      Products for Order:
                    </h4>
                    <div className="distribution-items-grid">
                      {order.items.map((item) => (
                        <div key={item.id} className="distribution-item-card">
                          <div className="distribution-item-header">
                            <div className="distribution-item-name">
                              {item.name}
                            </div>
                            {item.portionSize && (
                              <span className="distribution-item-portion">
                                {item.portionSize}
                              </span>
                            )}
                          </div>
                          <div className="distribution-item-content">
                            <div className="distribution-item-row">
                              <span className="distribution-item-label">
                                Quantity:
                              </span>
                              <span className="distribution-item-quantity">
                                {item.allocation.toLocaleString()}
                              </span>
                            </div>
                            <div className="distribution-item-row">
                              <span className="distribution-item-label">
                                Unit Price:
                              </span>
                              <span className="distribution-item-price">
                                Rs. {(item.unitPrice || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="distribution-item-row distribution-item-total">
                              <span className="distribution-item-label">
                                Total:
                              </span>
                              <span className="distribution-item-total-value">
                                Rs.{" "}
                                {(
                                  (item.allocation || 0) * (item.unitPrice || 0)
                                ).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Order Summary */}
                    <div className="distribution-order-summary">
                      <div className="distribution-summary-content">
                        <span className="distribution-summary-text">
                          {order.items.length} items,{" "}
                          {order.totalQuantity.toLocaleString()} total quantity
                        </span>
                        <span className="distribution-summary-total">
                          Total: Rs. {order.totalValue.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Modal Footer */}
        <div className="distribution-modal-footer">
          <div className="distribution-footer-left">
            {(() => {
              const confirmedOrders = orders.filter(
                (order) => order.status === "confirmed"
              ).length;
              const pendingOrders = orders.filter(
                (order) => order.status === "pending"
              ).length;
              const errorOrders = orders.filter(
                (order) => order.status === "error"
              ).length;
              return `${confirmedOrders} distributed, ${pendingOrders} pending, ${errorOrders} failed • ${selectedOrders.size} selected`;
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistributionModal;
